"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from "react";
import { api } from "~/utils/api";
import { useAppContext } from "./AppContext";

interface CustomTheme {
    id: string;
    name: string;
    css: string | null;
    activeFlag: boolean;
    iconUrl: string | null;
}

interface CustomThemeContextType {
    customThemes: CustomTheme[] | undefined;
    selectedThemeId: string;
    selectedTheme: CustomTheme | undefined;
    setSelectedThemeId: (id: string) => void;
    setCourseCss: (css: string | null) => void;
    /** Called by CourseLayout on unmount to signal leaving a course page */
    clearCourseCss: () => void;
    /** Raw CSS template with {Token} placeholders from CUSTOMIZABLE_COURTHEME_CSS_TEMPLATE */
    cssTemplate: string | null;
    /** The active course CSS, or null if not on a course page / course has no CSS */
    courseCss: string | null | undefined;
}

const CustomThemeContext = createContext<CustomThemeContextType>({
    customThemes: undefined,
    selectedThemeId: "",
    selectedTheme: undefined,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setSelectedThemeId: () => { },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setCourseCss: () => { },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    clearCourseCss: () => { },
    cssTemplate: null,
    courseCss: null,
});

// Persist the user's theme pick across full-page reloads (login redirect,
// Google Pay redirect) for THEME_TTL_MS, then revert to default.
const THEME_STORAGE_KEY = "selectedCourseTheme";
const THEME_TTL_MS = 2 * 60 * 60 * 1000;

// Cache the customTheme API responses so that on a full page reload we can
// resolve the persisted selectedThemeId to its CSS without waiting for the
// network. Cache is refreshed whenever the API returns fresh data.
const THEMES_CACHE_KEY = "customThemesCache";
const CSS_TEMPLATE_CACHE_KEY = "cssTemplateCache";

interface StoredTheme {
    selectedThemeId: string;
    hasUserSelected: boolean;
    // Theme CSS captured at selection time so the preload <script> can apply
    // it without waiting for the themes API. Independent of customThemesCache,
    // which the API call may not have populated yet on a fresh reload.
    css: string | null;
    timestamp: number;
}

// These helpers are called from useState initializers, which run on the
// server during SSR — guard with `typeof window` so they no-op there.
const readStoredTheme = (): StoredTheme | null => {
    if (typeof window === "undefined") return null;
    try {
        const raw = localStorage.getItem(THEME_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<StoredTheme>;
        if (
            typeof parsed?.timestamp !== "number" ||
            Date.now() - parsed.timestamp > THEME_TTL_MS
        ) {
            localStorage.removeItem(THEME_STORAGE_KEY);
            return null;
        }
        return {
            selectedThemeId: parsed.selectedThemeId ?? "",
            hasUserSelected: parsed.hasUserSelected ?? false,
            css: parsed.css ?? null,
            timestamp: parsed.timestamp,
        };
    } catch {
        return null;
    }
};

const writeStoredTheme = (selectedThemeId: string, hasUserSelected: boolean, css: string | null) => {
    if (typeof window === "undefined") return;
    try {
        localStorage.setItem(
            THEME_STORAGE_KEY,
            JSON.stringify({ selectedThemeId, hasUserSelected, css, timestamp: Date.now() } satisfies StoredTheme),
        );
    } catch {
        // ignore quota / privacy mode errors
    }
};

const readCachedThemes = (): CustomTheme[] | undefined => {
    if (typeof window === "undefined") return undefined;
    try {
        const raw = localStorage.getItem(THEMES_CACHE_KEY);
        return raw ? (JSON.parse(raw) as CustomTheme[]) : undefined;
    } catch {
        return undefined;
    }
};

const writeCachedThemes = (themes: CustomTheme[]) => {
    if (typeof window === "undefined") return;
    try {
        localStorage.setItem(THEMES_CACHE_KEY, JSON.stringify(themes));
    } catch {
        // ignore
    }
};

const readCachedCssTemplate = (): string | null => {
    if (typeof window === "undefined") return null;
    try {
        return localStorage.getItem(CSS_TEMPLATE_CACHE_KEY);
    } catch {
        return null;
    }
};

const writeCachedCssTemplate = (template: string) => {
    if (typeof window === "undefined") return;
    try {
        localStorage.setItem(CSS_TEMPLATE_CACHE_KEY, template);
    } catch {
        // ignore
    }
};

// Computes the initial selection state from localStorage. Used via useState
// lazy init so the first render already reflects the persisted pick — no
// flash, no "blank then fill" effect on full page reloads.
const getInitialThemeState = (): { selectedThemeId: string; hasUserSelected: boolean } => {
    const stored = readStoredTheme();
    if (stored?.hasUserSelected) {
        return { selectedThemeId: stored.selectedThemeId, hasUserSelected: true };
    }
    return { selectedThemeId: "", hasUserSelected: false };
};


export const CustomThemeProvider = ({ children }: { children: ReactNode }) => {
    // --- API responses with localStorage stale-while-revalidate ---
    // Cache the API responses so a full page reload (login redirect, GPay
    // redirect) can resolve the persisted selectedThemeId to its CSS without
    // waiting for the network. The cache is refreshed when the API resolves.
    const { data: customThemesApi } = api.customTheme.getActiveCustomThemes.useQuery({});
    // Initialized to undefined and hydrated from localStorage in useEffect so
    // the first client render matches SSR (where window is undefined).
    // Reading localStorage in the lazy init flips themesForPicker on/off
    // between server and client, breaking hydration of <MobileThemePicker>.
    const [cachedThemes, setCachedThemes] = useState<CustomTheme[] | undefined>(undefined);
    useEffect(() => {
        const cached = readCachedThemes();
        if (cached) setCachedThemes(cached);
    }, []);
    useEffect(() => {
        if (customThemesApi) {
            writeCachedThemes(customThemesApi);
            setCachedThemes(customThemesApi);
        }
    }, [customThemesApi]);
    const customThemes = customThemesApi ?? cachedThemes;

    const { data: cssTemplateData } = api.customTheme.getCssTemplate.useQuery({});
    const [cachedCssTemplate, setCachedCssTemplate] = useState<string | null>(null);
    useEffect(() => {
        const cached = readCachedCssTemplate();
        if (cached) setCachedCssTemplate(cached);
    }, []);
    useEffect(() => {
        if (cssTemplateData?.template) {
            writeCachedCssTemplate(cssTemplateData.template);
            setCachedCssTemplate(cssTemplateData.template);
        }
    }, [cssTemplateData?.template]);
    const cssTemplate = cssTemplateData?.template ?? cachedCssTemplate;

    // --- User selection: derived from localStorage on every mount via lazy init ---
    const [selectedThemeId, setSelectedThemeIdState] = useState<string>(() => getInitialThemeState().selectedThemeId);
    const [hasUserSelected, setHasUserSelected] = useState<boolean>(() => getInitialThemeState().hasUserSelected);
    // Mirror of the CSS captured at selection time. Lets activeCss render the
    // theme on first paint even while customThemes is still loading from the
    // API (e.g. when the request is batched behind slower/failed queries).
    const [storedCss, setStoredCss] = useState<string | null>(() => readStoredTheme()?.css ?? null);

    // The root layout injects a <style data-theme-preload> tag in <head> via
    // an inline script that runs before React hydrates. Wait until React's
    // own theme <style> is in the DOM before removing the preload — if we
    // remove it earlier, there's a window during API load where the theme
    // disappears and React's entity-color fallback flashes through.

    // Tri-state for course CSS:
    //   undefined  = not on a course page, OR waiting for course CSS (suppress all theme CSS)
    //   null       = on a course page but course has no CSS (fall back to defaultThemeCss)
    //   string     = course has CSS — always use this
    const [courseCss, setCourseCssState] = useState<string | null | undefined>(undefined);

    // Called by CourseLayout as soon as it knows the course CSS.
    // Passing null = "course has no CSS"; passing a string = "use this CSS".
    const setCourseCss = useCallback((css: string | null) => {
        setCourseCssState(css);
    }, []);

    // Called by CourseLayout on unmount/cleanup to go back to non-course state.
    const clearCourseCss = useCallback(() => {
        setCourseCssState(undefined);
    }, []);

    // When entering a course with CSS, reset selection back to default ("") unless user chose explicitly
    useEffect(() => {
        if (hasUserSelected) return;
        if (courseCss) {
            setSelectedThemeIdState("");
        }
    }, [courseCss, hasUserSelected]);

    const setSelectedThemeId = (id: string) => {
        // Capture the theme's CSS at selection time so the preload script and
        // the provider itself can apply it on the next reload without
        // depending on customThemesCache or the API call.
        const css = customThemes?.find((t) => t.id === id)?.css ?? null;
        setSelectedThemeIdState(id);
        setHasUserSelected(true);
        setStoredCss(css);
        writeStoredTheme(id, true, css);
    };

    const selectedTheme = customThemes?.find((t) => t.id === selectedThemeId);
    // "" = default/course mode; any explicit theme ID means the user wants that theme
    const isDefaultTheme = selectedThemeId === "" || !customThemes || customThemes.length === 0;

    // Determine which CSS to apply:
    //
    //   User picked a theme              → always use it (works on checkout/confirmation
    //                                      pages where CourseLayout doesn't mount)
    //   Default mode + courseCss undefined → suppress to avoid flash before course mounts
    //   Default mode + course CSS exists → course CSS wins
    //   Default mode + no course CSS    → no custom CSS (entity color applies via fallback)
    const activeCss = !isDefaultTheme
        // Prefer the live theme CSS once customThemes resolves, but fall back
        // to the CSS we stored at selection time so we render the theme even
        // while the API call is in flight.
        ? (selectedTheme?.css || storedCss || null)
        : courseCss === undefined
            ? null
            : (courseCss || null);

    // After mount, render React's own <style> tags. We don't render them
    // during SSR / first client render because the server has no access to
    // localStorage — rendering a server <style> guessing at the user's theme
    // creates a hydration mismatch and a visible flash. The preload <script>
    // in the root layout owns the head-level <style> tags until React mounts.
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    // Drop the preload tag only once React has its own theme <style> in the
    // DOM. Removing it earlier creates a gap where the theme disappears.
    useEffect(() => {
        if (mounted && activeCss) {
            document.querySelectorAll("style[data-theme-preload]").forEach((el) => el.remove());
        }
    }, [mounted, activeCss]);

    return (
        <CustomThemeContext.Provider
            value={{
                customThemes,
                selectedThemeId,
                selectedTheme,
                setSelectedThemeId,
                setCourseCss,
                clearCourseCss,
                cssTemplate,
                courseCss,
            }}
        >
            {children}
            {/*
              The preload <script> in the root layout owns:
                - the entity color fallback (<style data-entity-fallback>)
                - the user's selected theme (<style data-theme-preload>)
              Both live in <head> so they apply on first paint without any
              hydration mismatch. Once React mounts, we render the live
              theme <style> below and the cleanup effect drops the preload.
            */}
            {mounted && activeCss && (
                <style dangerouslySetInnerHTML={{ __html: activeCss }} />
            )}
        </CustomThemeContext.Provider>
    );
};

export const useCustomThemeContext = () => {
    return useContext(CustomThemeContext);
};
